/**
 * ReleasesStrip - "Coming Soon" rail of admin-managed upcoming singles/albums,
 * pinned near the top of the Live page. Fans can get reminded and pre-save.
 * Admins get an Add/Manage button to create/edit/delete releases. Hidden when
 * empty (unless you're an admin, so you can add the first one).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Disc3, Calendar, Loader2, BellRing, Bell, ExternalLink, Plus, X, Trash2, Pencil, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { enablePush, isPushSupported } from '../utils/push';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABEL = { single: 'Single', album: 'Album' };

const countdown = (dateStr) => {
  const target = new Date(`${String(dateStr).slice(0, 10)}T00:00:00Z`).getTime();
  const diff = target - Date.now();
  if (diff <= 0) return 'Today';
  const d = Math.ceil(diff / 86400000);
  return d >= 1 ? `in ${d}d` : 'Today';
};

export const ReleaseCard = ({ rel, onNotify, onEdit, isAdmin, busy }) => {
  const outNow = rel.status === 'out_now';
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="pointer-events-auto shrink-0 w-52 bg-black/30 backdrop-blur border border-white/5 rounded-xl overflow-hidden flex opacity-75 hover:opacity-100 transition-opacity"
      data-testid={`release-card-${rel.id}`}
    >
      <div className="w-14 h-14 shrink-0 bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/20 relative">
        {rel.cover_url
          ? <img src={rel.cover_url} alt={rel.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Disc3 className="w-5 h-5 text-white/60" /></div>}
        {outNow && (
          <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-[#39FF14] text-black text-[8px] font-black tracking-wide">OUT NOW</span>
        )}
      </div>
      <div className="flex-1 min-w-0 p-1.5">
        <div className="flex items-center gap-1 mb-0.5">
          <span className="px-1.5 py-0.5 rounded-full bg-[#00F0FF]/20 text-[#00F0FF] text-[9px] font-bold uppercase tracking-wide">
            {TYPE_LABEL[rel.release_type] || 'Release'}
          </span>
          {!outNow && (
            <span className="text-[#39FF14] text-[10px] font-semibold flex items-center gap-0.5">
              <Calendar className="w-2.5 h-2.5" />{countdown(rel.release_date)}
            </span>
          )}
          {isAdmin && (
            <button onClick={() => onEdit(rel)} className="ml-auto text-white/40 hover:text-white" data-testid={`release-edit-${rel.id}`}>
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
        <p className="text-white text-xs font-bold truncate leading-tight">{rel.title}</p>
        <p className="text-white/50 text-[10px] truncate mb-1">
          {rel.artist_name || ' '}
          {rel.notify_count > 0 && (
            <span className="ml-1 text-[#FF007F] font-bold" data-testid={`release-waiting-${rel.id}`}>· 🔔 {rel.notify_count} waiting</span>
          )}
        </p>
        <div className="flex gap-1">
          <button
            onClick={() => onNotify(rel)}
            disabled={busy === rel.id}
            className={`flex-1 py-1 rounded-full text-[10px] font-bold flex items-center justify-center gap-1 transition-colors ${rel.notified ? 'bg-[#FF007F] text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
            data-testid={`release-notify-${rel.id}`}
          >
            {busy === rel.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (rel.notified ? <BellRing className="w-3 h-3" /> : <Bell className="w-3 h-3" />)}
            {rel.notified ? 'Reminding' : 'Remind me'}
          </button>
          {rel.link && (
            <a href={rel.link} target="_blank" rel="noopener noreferrer"
              className="py-1 px-2 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-[10px] font-bold flex items-center gap-1"
              data-testid={`release-presave-${rel.id}`}>
              <ExternalLink className="w-3 h-3" />Pre-save
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const emptyForm = { title: '', release_type: 'single', release_date: '', description: '', link: '', artist_name: '', cover_url: '', tracklist: '' };

export const ManageModal = ({ onClose, onSaved, editing }) => {
  const [form, setForm] = useState(editing
    ? { ...editing, tracklist: (editing.tracklist || []).join('\n') }
    : emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/upcoming-releases/upload-cover`, fd, auth);
      set('cover_url', res.data.url);
    } catch { toast.error('Cover upload failed'); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title.trim() || !form.release_date) { toast.error('Title and release date are required'); return; }
    setSaving(true);
    const payload = { ...form, tracklist: form.tracklist.split('\n').map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing) await axios.put(`${API_URL}/api/upcoming-releases/${editing.id}`, payload, auth);
      else await axios.post(`${API_URL}/api/upcoming-releases`, payload, auth);
      toast.success(editing ? 'Release updated' : 'Release added');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const del = async () => {
    if (!editing || !window.confirm('Delete this release?')) return;
    try { await axios.delete(`${API_URL}/api/upcoming-releases/${editing.id}`, auth); toast.success('Deleted'); onSaved(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="release-manage-modal">
      <div className="w-full max-w-md bg-[#12101A] rounded-t-3xl sm:rounded-3xl border border-white/10 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#12101A]">
          <h3 className="text-white font-bold">{editing ? 'Edit release' : 'New release'}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-3 items-center">
            <label className="w-20 h-20 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center cursor-pointer overflow-hidden shrink-0">
              {form.cover_url ? <img src={form.cover_url} alt="" className="w-full h-full object-cover" />
                : (uploading ? <Loader2 className="w-5 h-5 animate-spin text-white/60" /> : <Upload className="w-5 h-5 text-white/50" />)}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="release-cover-input" />
            </label>
            <div className="flex-1 space-y-2">
              <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Title" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="release-title-input" />
              <input value={form.artist_name} onChange={(e) => set('artist_name', e.target.value)} placeholder="Artist name" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <select value={form.release_type} onChange={(e) => set('release_type', e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="release-type-select">
              <option value="single">Single</option>
              <option value="album">Album</option>
            </select>
            <input type="date" value={String(form.release_date).slice(0, 10)} onChange={(e) => set('release_date', e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="release-date-input" />
          </div>
          <textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Short description" rows={2} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          <input value={form.link} onChange={(e) => set('link', e.target.value)} placeholder="Pre-save / Spotify / Apple link" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          {form.release_type === 'album' && (
            <textarea value={form.tracklist} onChange={(e) => set('tracklist', e.target.value)} placeholder="Tracklist (one per line)" rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          )}
          <div className="flex gap-2 pt-1">
            {editing && <button onClick={del} className="p-2.5 rounded-xl bg-red-500/15 text-red-400 hover:bg-red-500/25" data-testid="release-delete-btn"><Trash2 className="w-4 h-4" /></button>}
            <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-bold text-sm disabled:opacity-60 flex items-center justify-center gap-2" data-testid="release-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save changes' : 'Add release')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReleasesStrip = () => {
  const { user } = useAuth();
  const isAdmin = !!(user && (user.isSuperAdmin || user.isAdmin || user.role === 'super_admin' || user.role === 'admin'));
  const [releases, setReleases] = useState([]);
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null); // 'new' | release obj | null

  const load = useCallback(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/upcoming-releases`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then((r) => setReleases(r.data?.releases || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const notify = async (rel) => {
    const token = localStorage.getItem('token');
    if (!token) { window.location.href = '/auth'; return; }
    setBusy(rel.id);
    try {
      const res = await axios.post(`${API_URL}/api/upcoming-releases/${rel.id}/notify`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setReleases((rs) => rs.map((r) => r.id === rel.id ? { ...r, notified: res.data.notified, notify_count: res.data.notify_count } : r));
      if (res.data.notified) {
        toast.success("We'll remind you when it drops!");
        // Offer real device push so fans get pinged even with the app closed
        if (isPushSupported()) {
          try {
            await enablePush();
            toast.success('Push alerts on 🔔 — you\'ll get pinged the moment it drops');
          } catch (e) {
            if (e?.message === 'denied') {
              toast.message('Allow notifications to get a live ping — we\'ll still email you.');
            }
            // unsupported (e.g. iOS not installed to Home Screen): email + in-app still cover it
          }
        }
      } else {
        toast.success('Reminder off');
      }
    } catch { toast.error('Try again'); }
    setBusy(null);
  };

  if (releases.length === 0 && !isAdmin) return null;

  return (
    <div className="fixed top-[164px] left-0 right-0 z-30 pointer-events-none" data-testid="releases-strip">
      <div className="flex items-center gap-1.5 px-4 mb-1.5 pointer-events-auto">
        <Disc3 className="w-3.5 h-3.5 text-[#FF007F]" />
        <span className="text-white/80 text-[11px] font-bold uppercase tracking-widest">Coming Soon</span>
        {isAdmin && (
          <button onClick={() => setModal('new')} className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold" data-testid="release-manage-btn">
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>
      {releases.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar pointer-events-auto">
          {releases.map((rel) => (
            <ReleaseCard key={rel.id} rel={rel} onNotify={notify} onEdit={(r) => setModal(r)} isAdmin={isAdmin} busy={busy} />
          ))}
        </div>
      )}
      <AnimatePresence>
        {modal && (
          <ManageModal
            editing={modal === 'new' ? null : modal}
            onClose={() => setModal(null)}
            onSaved={() => { setModal(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReleasesStrip;
